import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AddDesignToCollection from '@components/design-uploads/AddDesignToCollection';
import DownloadPresentation from '@components/DownloadPresentation';
import BottomPopup from '@components/modals/BottomPopup';
import TimeBasedLoader from '@components/TimeBasedLoader';
import { toastError, toastSuccess, toastWarning } from '@components/Toast';

import { useCommonTags } from '@hooks/useCommonTags';

import CreateSetOrder from '@features/collection/details/CreateSetOrder';
import DeleteSetOrder from '@features/collection/details/DeleteSetOrder';
import TagUpdateModal from '@features/collection/details/TagUpdateModal';
import UploadZalandoPO from '@features/zalandoPOUpload/components/UploadZalandoPO';
import SampleAdd from '@features/sampleDevelopment/components/SampleAdd';
import CostingAdd from '@features/collection/components/CostingAdd';
import CreateOrderModal from '@features/order/components/CreateOrderModal';

import { storeProductData } from '@store/actions/DesignAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { createSetOrder, removeProductsFromCollection } from '@services/collectionService';

import ApiClient from '@lib/apiClient';

import { isShowDraftButton } from '@utils/helper';
import { capitalizeFirstLetter, printApiError } from '@utils/Util';

import AddTaskModal from '@/features/Scan/components/AddTaskModal';
import CreateNewTask from '@/features/Scan/components/CreateNewTask';
import { useDownloadTpSummaryMutation } from '@/api/commonDownloadsApi';

const isDraftForMultipleSelection = (products, selectedProductIds, requestFromParam) => {
	if (products.length === 0 || selectedProductIds.length === 0) return [];

	const filteredList = products.filter((product) => selectedProductIds.includes(product.id));

	if (filteredList.length === 0) return [];

	const productWithDraftButtonHidden = filteredList.find(
		(product) => !isShowDraftButton(product, requestFromParam)
	);

	if (productWithDraftButtonHidden) return true;
	return false;
};

const StyleDetailsBottomActions = ({
	productList = [],
	setProductList,
	collectionInfo,
	isHideSetOrder,
	additional = {},
	requestType,
	setProductAsDraft,
	productWiseTags,
	updateTagList,
	fetchAllTags,
	requestFromParam,
	page,
	totalElements,
	getCollectionProducts,
	setIsPlay,
	isSelectOnNextPage,
	setIsSelectOnNextPage,
	setIsShowPublishDesign,
	artboardProducts
}) => {
	const selectedProductIds = useSelector((store) => store.product.selectedProductIds);
	const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState(false);
	const [showAddCollectionPopup, setShowAddCollectionPopup] = useState(false);
	const [requestTypeForSetOrder, setRequestTypeForSetOrder] = useState(null);
	const [showAddSamplePopup, setShowAddSamplePopup] = useState(false);
	const [showCostingPopup, setShowCostingPopup] = useState(false);
	const [isOpenSetOrder, setIsOpenSetOrder] = useState(false);
	const [downloadFlag, setDownloadFlag] = useState(false);
	const [startLoader, setStartLoader] = useState(false);
	const [setOrderType, setSetOrderType] = useState('');
	const [openModal, setOpenModal] = useState(false);
	const [isOpenBulkTag, setIsOpenBulkTag] = useState(false);
	const [isUnSelectAll, setIsUnSelectAll] = useState(false);
	const [isOpenPlaceOrder, setIsOpenPlaceOrder] = useState(false);
	const [isShowTaskUpdateModal, setIsShowTaskUpdateModal] = useState(false);
	const [isShowCreateNewTaskModal, setIsShowCreateNewTaskModal] = useState(false);
	const [taskInputValueFromTaskModal, setTaskInputValueFromTaskModal] = useState('');

	const [downloadTpSummary] = useDownloadTpSummaryMutation();

	useEffect(() => {
		if (isSelectOnNextPage) {
			return additional?.handleSelectAll();
		}
	}, [isSelectOnNextPage, productList?.length]);

	const commonTags = useCommonTags(productList, selectedProductIds);

	const dispatch = useDispatch();

	const downloadReportByProductIds = () => {
		downloadTpSummary({ productIds: selectedProductIds });
	};

	const clearFabricStatus = () => {
		return {
			type: 'SET_PRODUCT_DATA',
			payload: {
				key: 'productsWithoutBodyFabric',
				data: []
			}
		};
	};

	const onClearSelectedProducts = async () => {
		await dispatch(storeProductData('selectedProductIds', []));
		await dispatch(clearFabricStatus());
	};

	const onSuccessBdSend = async () => {
		let orderedProductList = [];
		orderedProductList = productList?.map((product) => {
			if (selectedProductIds.includes(product.id)) {
				product.availabilityStatus = 'SENT_TO_BD';
				return {
					...product
				};
			} else return product;
		});
		setProductList(orderedProductList);
		await onClearSelectedProducts();
	};

	const onSendBd = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('sendToBd', selectedProductIds);
			toastSuccess(data?.message);
			await onSuccessBdSend();
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			printApiError(error);
		}
	};

	const removeDesigns = async () => {
		try {
			dispatch(openLoader());
			await removeProductsFromCollection({
				id: additional.id,
				productIds: selectedProductIds
			});
			await additional.onAddNewDesignModal();
			dispatch(closeLoader());
		} catch (e) {
			printApiError(e);
			dispatch(closeLoader());
		}
	};

	const createPack = async () => {
		try {
			dispatch(openLoader());
			let selectedProduct = productList?.find((item) => item.id === selectedProductIds[0]);
			if (selectedProduct !== undefined) {
				let params = `?entityId=${collectionInfo?.id}&entityType=COLLECTION&name=${encodeURIComponent(selectedProduct.referenceNumber)}&productId=${
					selectedProduct.id
				}`;
				const data = await createSetOrder('create-pack', params);
				additional.updateProductCard(data, 'PACK', { type: 'CREATE_SET_OR_PACK' });
				await onClearSelectedProducts();
			}
			dispatch(closeLoader());
		} catch (error) {
			printApiError(error);
			dispatch(closeLoader());
		}
	};

	const selectProductsAsDraft = async () => {
		if (isDraftForMultipleSelection(productList, selectedProductIds, requestFromParam)) {
			toastError(`Some selected designs can't be drafted with their current status`);
		} else {
			setProductAsDraft();
		}
	};

	const getProductWithNoBodyFabric = () => {
		const listOfProduct = [];
		productList.forEach((product) => {
			if (selectedProductIds.includes(product?.id)) {
				if (product?.isSet) {
					return product?.setMemberProducts.forEach((member) => {
						if (!member.fabricDetails && !member.fabricName) {
							listOfProduct.push(product);
						}
					});
				} else {
					if (!product.fabricDetails && !product.fabricName) {
						listOfProduct.push(product);
					}
				}
			}
		});
		return listOfProduct;
	};

	const onQuoteSampleRequest = (requestType) => {
		const productWithoutBodyFabric = getProductWithNoBodyFabric();
		if (productWithoutBodyFabric.length > 0) {
			dispatch(openLoader());
			dispatch({
				type: 'SET_PRODUCT_DATA',
				payload: {
					key: 'productsWithoutBodyFabric',
					data: productWithoutBodyFabric
				}
			});
			toastError(`Please add body fabric for ${capitalizeFirstLetter(requestType)} request.`);
			dispatch(closeLoader());
			return;
		}

		if (requestType === 'QUOTE') {
			setShowCostingPopup(true);
		} else {
			setShowAddSamplePopup(true);
		}
	};
	const getProductWithSoldArtwork = () => {
		return selectedProductIds?.some((id) => {
			const artboardProduct = artboardProducts[id];
			return artboardProduct && artboardProduct.status === 'SOLD';
		});
	};

	const onPlaceOrder = () => {
		const productWithoutBodyFabric = getProductWithNoBodyFabric();
		const soldArtworks = getProductWithSoldArtwork();

		if (soldArtworks) {
			toastWarning(
				'This artwork has already been sold and cannot be ordered. Please select a different artwork.'
			);
			return;
		}

		if (productWithoutBodyFabric.length > 0) {
			dispatch(openLoader());
			dispatch({
				type: 'SET_PRODUCT_DATA',
				payload: {
					key: 'productsWithoutBodyFabric',
					data: productWithoutBodyFabric
				}
			});
			toastError('Please add body fabric for place order.');
			dispatch(closeLoader());
		} else {
			let soldIds = productList
				.filter((item) => item.availabilityStatus === 'SOLD')
				?.map((item) => item.id);

			let unsoldIds = productList
				.filter((item) => item.availabilityStatus !== 'SOLD')
				?.map((item) => item.id);

			let isSelectedSoldIdExist = selectedProductIds.some((id) => soldIds.includes(id));
			let isSelectedUnsoldIdExist = selectedProductIds.some((id) => unsoldIds.includes(id));

			if (isSelectedSoldIdExist && isSelectedUnsoldIdExist) {
				toastError("Can't place order for sold and unsold styles together.");
			} else if (isSelectedSoldIdExist || isSelectedUnsoldIdExist) {
				setIsOpenPlaceOrder(true);
			}
		}
	};

	const onActionClick = async (type) => {
		switch (type) {
			case 'SELECT_ALL':
				setIsUnSelectAll(true);
				await additional.handleSelectAll();
				break;
			case 'FETCH_NEXT_PAGE':
				getCollectionProducts(page + 1);
				setIsSelectOnNextPage(true);
				break;
			case 'PLAY_PRESENTATION':
				setIsPlay(true);
				break;
			case 'COLLECTION':
				setShowAddCollectionPopup(true);
				break;
			case 'PACK':
				await createPack();
				break;
			case 'SET':
				setRequestTypeForSetOrder('CREATE');
				setSetOrderType('SET');
				setIsOpenSetOrder(true);
				break;
			case 'SAMPLE':
				onQuoteSampleRequest('SAMPLE');
				break;
			case 'COSTING':
				onQuoteSampleRequest('QUOTE');
				break;
			case 'PLACE_ORDER':
				onPlaceOrder();
				break;
			case 'ZALANDO':
				const soldArtworks = getProductWithSoldArtwork();

				if (soldArtworks) {
					toastWarning('You can not place order of sold artwork!');
					return;
				}
				setOpenModal(true);
				break;
			case 'SEND_BD':
				await onSendBd();
				break;
			case 'DOWNLOAD_REPORT':
				await downloadReportByProductIds();
				break;
			case 'CLEAR':
				setIsUnSelectAll(false);
				await onClearSelectedProducts();
				break;
			case 'REMOVE_PRODUCT':
				await removeDesigns();
				break;
			case 'DO_ALL_DRAFT':
				await selectProductsAsDraft();
				break;
			case 'BULK_TAG':
				setIsOpenBulkTag(true);
				break;
			case 'PUBLISH':
				setIsShowPublishDesign(true);
				break;
			case 'TASK_UPDATE':
				setIsShowTaskUpdateModal(true);
			default:
				break;
		}
	};

	const getRemainingStyles = () => {
		const elements = totalElements || 0;
		const selectedCount = selectedProductIds?.length || 0;
		const remaining = elements - selectedCount;

		return remaining < 30 ? remaining : 30;
	};

	const onStartDownload = () => {
		setDownloadFlag(false);
		setStartLoader(true);
	};

	const onDownload = async () => {
		setStartLoader(false);
		dispatch(storeProductData('selectedProductIds', []));
	};

	const isShowRepeatOrderTag = () => {
		let soldIds = productList
			.filter((item) => item.availabilityStatus === 'SOLD')
			?.map((item) => item.id);
		return selectedProductIds.some((id) => soldIds.includes(id));
	};

	return (
		<>
			{isOpenBulkTag && (
				<TagUpdateModal
					type="ADD_NEW"
					title="Tag"
					onCancel={async () => {
						await setIsOpenBulkTag(false);
						await onClearSelectedProducts();
					}}
					allTagList={productWiseTags}
					commonTags={commonTags}
					updateTagList={updateTagList}
					fetchAllTags={fetchAllTags}
				/>
			)}

			{selectedProductIds && selectedProductIds.length > 0 && (
				<BottomPopup
					isHideSetOrder={isHideSetOrder}
					length={`${selectedProductIds?.length}/${totalElements}`}
					type={requestType}
					isZalando={collectionInfo?.brand?.toUpperCase() === 'ZALANDO'}
					onActionClick={(type) => onActionClick(type)}
					showUnSelectAll={isUnSelectAll}
					remainingStyles={getRemainingStyles()}
					isDisableFetchNextPage={getRemainingStyles() === 0}
				/>
			)}
			{downloadFlag && (
				<DownloadPresentation
					isDownLoad={downloadFlag}
					onStartDownload={onStartDownload}
					onClose={async () => {
						setDownloadFlag(false);
						setStartLoader(false);
						await onClearSelectedProducts();
					}}
					collectionTitle={collectionInfo.name}
					selectedProductIds={selectedProductIds}
					onDownload={onDownload}
				/>
			)}
			{startLoader && <TimeBasedLoader startLoader={startLoader} />}
			<UploadZalandoPO
				show={openModal}
				handleClose={() => setOpenModal(false)}
				brandId={collectionInfo?.brandId}
			/>
			{isOpenPlaceOrder && (
				<CreateOrderModal
					isShowRepeatOrderTag={isShowRepeatOrderTag()}
					onClose={async () => {
						setIsOpenPlaceOrder(false);
						onClearSelectedProducts();
					}}
					brandId={collectionInfo?.brandId}
					collectionId={collectionInfo?.id}
				/>
			)}
			{showAddSamplePopup && (
				<SampleAdd
					requestType="SAMPLE"
					requestFrom="COLLECTION"
					source="DESIGN"
					selectedDesignIds={selectedProductIds}
					onClose={async () => {
						setShowAddSamplePopup(false);
						await onClearSelectedProducts();
					}}
				/>
			)}

			{showCostingPopup && (
				<CostingAdd
					requestFrom="COLLECTION"
					selectedDesignIds={selectedProductIds}
					onClose={async () => {
						setShowCostingPopup(false);
						await onClearSelectedProducts();
					}}
				/>
			)}
			{showAddCollectionPopup && (
				<AddDesignToCollection
					type="ADD_NEW"
					title="Copy or move to collection"
					onAddDesign={async () => {
						await setShowAddCollectionPopup(false);
						await onClearSelectedProducts();
					}}
					onCancel={async () => {
						await setShowAddCollectionPopup(false);
						await onClearSelectedProducts();
					}}
					designIdList={selectedProductIds}
				/>
			)}
			{isOpenSetOrder && (
				<CreateSetOrder
					onSubmitSuccess={onClearSelectedProducts}
					updateProductCard={additional.updateProductCard}
					requestType={requestTypeForSetOrder}
					productIds={selectedProductIds}
					selectedProduct={additional.selectedStyle}
					setOrderType={setOrderType}
					onClose={() => {
						additional.setSelectedStyle(null);
						setSetOrderType('');
						setRequestTypeForSetOrder('');
						setIsOpenSetOrder(false);
					}}
					entityId={additional.id}
					entityType="COLLECTION"
				/>
			)}
			{isOpenDeleteConfirmation && (
				<DeleteSetOrder
					selectedProduct={additional.selectedStyle}
					setOrderType={setOrderType}
					onClose={() => {
						additional.setSelectedStyle(null);
						setSetOrderType('');
						setIsOpenDeleteConfirmation(false);
					}}
					updateProductCard={additional.updateProductCard}
				/>
			)}
			{isShowTaskUpdateModal && (
				<AddTaskModal
					onClose={(taskName) => {
						if (taskName) {
							setTaskInputValueFromTaskModal(taskName);
						}
						setIsShowTaskUpdateModal(false);
					}}
					onOpenAddNewTaskModal={() => setIsShowCreateNewTaskModal(true)}
					productIdList={selectedProductIds}
				/>
			)}
			{isShowCreateNewTaskModal && (
				<CreateNewTask
					onClose={() => setIsShowCreateNewTaskModal(false)}
					onOpenTaskModal={() => setIsShowTaskUpdateModal(true)}
					taskInputValueFromTaskModal={taskInputValueFromTaskModal}
				/>
			)}
		</>
	);
};

export default StyleDetailsBottomActions;
